/**
 * The login screen.
 *
 * FR-8.1b's first half lives here, and it is deliberately unhelpful: a member's
 * mobile number and password produce the SAME generic sentence as a wrong
 * password, because `authenticate_admin` refuses a non-admin before any copy can
 * distinguish the case. The screen shows the server's sentence and adds nothing -
 * no "are you a member? try the app" hint, which would confirm the account exists
 * and defeat the point.
 *
 * The second half of FR-8.1b (an admin's credentials have no special powers in
 * the member app) is enforced on the API's `/auth/login`, which refuses `role =
 * admin` with the same generic 401. Nothing needs doing here.
 */

import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, ErrorNotice, Field, Input } from "../components/primitives";
import { useAuth } from "../lib/auth";

type LocationState = { from?: string } | null;

export function LoginScreen() {
  const { signIn, signedOutReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signIn(identifier.trim(), password);
      // Back where they were headed, or the dashboard.
      const state = location.state as LocationState;
      navigate(state?.from ?? "/", { replace: true });
    } catch (caught: unknown) {
      setError(caught);
      // The password is cleared on failure and the identifier is kept: a retry
      // after a typo should not mean retyping both.
      setPassword("");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="login">
      <div className="login__panel">
        <div className="login__brand">
          <div className="wordmark login__wordmark">
            <span>fitzone</span>
            <span className="wordmark__care">care</span>
          </div>
          <p className="login__scope">
            Administrator sign-in. Members use the Fitzone Care mobile app.
          </p>
        </div>

        {signedOutReason ? <div className="alert alert--info">{signedOutReason}</div> : null}

        <form className="card" onSubmit={onSubmit} noValidate>
          <div className="stack" style={{ gap: 16 }}>
            <Field label="Mobile number or email">
              {(id) => (
                <Input
                  id={id}
                  name="identifier"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                  maxLength={254}
                  disabled={pending}
                />
              )}
            </Field>

            <Field label="Password">
              {(id) => (
                <Input
                  id={id}
                  name="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                  maxLength={200}
                  disabled={pending}
                />
              )}
            </Field>

            <ErrorNotice error={error} />

            <Button type="submit" variant="primary" className="btn--block" disabled={pending}>
              {pending ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>

        <p className="text-meta text-small">
          There is no self-service sign-up for administrators, and no password reset
          here. Ask another administrator to change your password from Members.
        </p>
      </div>
    </div>
  );
}
